import { ReactNode, useEffect, useMemo } from 'react';

import { GetWorkOrdersResponseDTO, MODULE } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { ColumnFiltersState, RowSelectionState } from '@tanstack/react-table';

import excavatorVector from '@/assets/vectors/excavator.svg';
import manholeShrugVector from '@/assets/vectors/manhole-shrug.svg';
import UndoIcon from '@/components/icons/undo';
import { TablePageTable } from '@/components/table-page/table-page-table';
import { useModulesStore } from '@/stores/modules.store';

import { columns, createWorkOrdersTableColumns } from './table-meta';

export function WorkOrdersTable({
  notFoundDisplay,
  data,
  loading,
  onRowSelectionChange,
  onRowClick,
  rowSelection,
  columnFilters,
  onColumnFiltersChange,
  sorting,
  grouping,
  columnOrder,
  columnVisibility,
  onSortingChange,
}: {
  notFoundDisplay: ReactNode;
  data: GetWorkOrdersResponseDTO['data'];
  loading: boolean;
  onRowSelectionChange: (selectedRows: RowSelectionState) => void;
  onRowClick: (row: GetWorkOrdersResponseDTO['data'][number]) => void;
  rowSelection: RowSelectionState;
  columnFilters: ColumnFiltersState;
  onColumnFiltersChange: (columnFilters: ColumnFiltersState) => void;
  sorting: { key?: string; direction?: 'asc' | 'desc' };
  grouping: string[];
  columnOrder: Array<{ id: string; active: boolean }>;
  columnVisibility: Record<string, boolean>;
  onSortingChange: (sorting: { key?: string; direction?: 'asc' | 'desc' }) => void;
}) {
  const { syncModuleColumns } = useModulesStore();

  const initialSorting = useMemo(
    () =>
      sorting.key
        ? [
            {
              id: sorting.key,
              desc: sorting.direction === 'desc',
            },
          ]
        : [],
    [sorting.key, sorting.direction],
  );

  useEffect(() => {
    syncModuleColumns(MODULE.WorkOrders, columns);
  }, [syncModuleColumns]);

  return (
    <div className="flex-1 bg-white">
      <TablePageTable<GetWorkOrdersResponseDTO['data'][number]>
        data={data}
        columns={createWorkOrdersTableColumns()}
        notFoundDisplay={notFoundDisplay}
        loading={loading}
        onSortingChange={onSortingChange}
        initialSorting={initialSorting}
        grouping={grouping}
        columnOrder={(columnOrder || []).map((column) => column.id)}
        onRowClick={onRowClick}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        columnVisibility={columnVisibility}
        columnFilters={columnFilters}
        onColumnFiltersChange={onColumnFiltersChange}
      />
    </div>
  );
}

export function NoWorkOrdersCreatedDisplay({ openWorkOrderDrawer }: { openWorkOrderDrawer: () => void }) {
  return (
    <div className="absolute inset-0 m-auto flex w-[383px] flex-col items-center justify-center gap-[20px]">
      <img src={excavatorVector} alt="Excavator" className="h-[114.5px] w-[220px]" />
      <span className="text-xl leading-[30px] font-medium">No work orders have been created yet</span>
      <div className="pointer-events-auto flex gap-1">
        <Button variant="success" onClick={openWorkOrderDrawer}>
          Create Work Order
        </Button>
      </div>
    </div>
  );
}

export function NoWorkOrdersFoundFromSearchDisplay() {
  return (
    <div className="absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={manholeShrugVector} alt="Manhole shrug" className="h-28" />
      <span className="text-sm">No work orders match your search</span>
    </div>
  );
}

export function NoWorkOrdersFoundFromFiltersDisplay({
  openWorkOrderDrawer,
  removeColumnFilters,
}: {
  openWorkOrderDrawer: () => void;
  removeColumnFilters: () => void;
}) {
  return (
    <div className="bg-grey-1 absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={manholeShrugVector} alt="Manhole shrug" className="h-28" />
      <span className="text-xl font-medium">No work orders match your filters</span>
      <span className="text-secondary-text text-sm">Try adjusting your column filters or create a new work order.</span>
      <div className="pointer-events-auto flex gap-2">
        <Button variant="grey" onClick={removeColumnFilters} className="gap-1 font-normal">
          <UndoIcon />
          Remove all filters
        </Button>
        <Button variant="success" onClick={openWorkOrderDrawer}>
          Create Work Order
        </Button>
      </div>
    </div>
  );
}
